export type BlueprintLayerKey = 'ethizo' | 'azure' | 'data' | 'apps' | 'ai';

export interface BlueprintLayer {
  key: BlueprintLayerKey;
  step: string;
  label: string;
  title: string;
  summary: string;
  technologies: string[];
  responsibilities: string[];
  controls: string[];
  outcomes: string[];
  ownership: string;
}

export const blueprintLayers: BlueprintLayer[] = [
  {
    key: 'ethizo', step: '01 · System of record', label: 'EH', title: 'Ethizo + vendor systems',
    summary: 'Clinical record, scheduling, billing, and vendor-owned workflows remain authoritative.',
    technologies: ['FHIR R4', 'US Core 6.1.0', 'SMART OAuth2/OIDC', 'Read/search', 'Group export'],
    responsibilities: ['Maintain clinical truth', 'Expose contractually approved interfaces', 'Preserve source identifiers and versions'],
    controls: ['SMART scope validation', 'Tenant and resource confirmation', 'Contract, BAA, rate limits, and data rights'],
    outcomes: ['Authorized exchange proof', 'Reduced UI dependence', 'Stable source authority'],
    ownership: 'Ethizo owns the clinical record; each connected vendor remains authoritative for its domain.'
  },
  {
    key: 'azure', step: '02 · Trust + interoperability', label: 'AZ', title: 'Azure Health Data Services',
    summary: 'Managed FHIR API, Entra authorization, access auditing, private connectivity, and elastic exchange.',
    technologies: ['FHIR service', 'Private Link', 'Entra RBAC', 'Diagnostic logs', 'Built-in compute autoscaling'],
    responsibilities: ['Validate and normalize approved exchange', 'Enforce identity and network boundaries', 'Record access and operational evidence'],
    controls: ['Private network posture', 'Managed identities', 'Audit retention and alerting'],
    outcomes: ['Fast provisioning', 'Elastic API capacity', 'Consistent trust controls'],
    ownership: 'The interoperability layer does not replace source ownership or grant downstream use rights.'
  },
  {
    key: 'data', step: '03 · eMerge data platform', label: 'DP', title: 'Canonical context + data products',
    summary: 'Stable facility, referral, appointment, encounter, claim, workflow, and lineage models.',
    technologies: ['ADLS Gen2', 'Azure SQL / Fabric', 'Purview', 'Service Bus / Event Grid'],
    responsibilities: ['Preserve immutable evidence', 'Map stable eMerge contracts', 'Publish minimum-necessary data products'],
    controls: ['Source authority per field', 'Classification and retention', 'Versioned schemas and lineage'],
    outcomes: ['Vendor-neutral views', 'Reusable contracts', 'Proprietary operating memory'],
    ownership: 'eMerge owns canonical orchestration context, while source facts retain their authoritative lineage.'
  },
  {
    key: 'apps', step: '04 · Operating surfaces', label: 'UX', title: 'Dashboards, CRM, and queues',
    summary: 'Role-specific work surfaces coordinate action and remove noisy vendor UI.',
    technologies: ['React', 'ASP.NET Core', 'Power BI', 'API Management'],
    responsibilities: ['Present role-specific context', 'Coordinate accountable workflows', 'Capture decisions and exceptions'],
    controls: ['Role and purpose access', 'Human approvals', 'Stable API boundaries'],
    outcomes: ['Fewer manual handoffs', 'Unified facility lifecycle', 'Cycle-time reduction'],
    ownership: 'Work surfaces coordinate action but do not become a shadow clinical or billing system.'
  },
  {
    key: 'ai', step: '05 · Controlled intelligence', label: 'AI', title: 'Governed agents and analysis',
    summary: 'Purpose-bound retrieval, recommendations, summaries, and workflow assistance.',
    technologies: ['Azure AI Search', 'Model gateway', 'Metadata prefilters', 'Security trimming', 'Human review'],
    responsibilities: ['Accept prefiltered context only', 'Validate outputs and citations', 'Escalate accountable actions'],
    controls: ['AI exposure class', 'Identity/role/purpose filters', 'Output validation and failure behavior'],
    outcomes: ['Context-aware assistance', 'Faster coordination', 'No unrestricted PHI access'],
    ownership: 'Models never select their own permissions; policy and authorized people remain the action boundary.'
  }
];

export const trustZones = [
  ['Z0', 'Authoritative systems', 'Ethizo, CRM, RCM, scheduling, imaging, and Microsoft 365'],
  ['Z1', 'Restricted identity + PHI', 'Direct identifiers and re-identification mappings; narrowest access'],
  ['Z2', 'Immutable raw evidence', 'Approved source payload, version, checksum, lineage, validation, and quarantine'],
  ['Z3', 'Canonical + tokenized', 'Stable eMerge IDs and vendor-neutral operational relationships'],
  ['Z4', 'Curated data products', 'Facility, capacity, launch, quality, revenue, and executive views'],
  ['Z5', 'AI-approved context', 'Approved fields, records, purposes, roles, facilities, and retention only']
] as const;

export const accessRequirements = [
  ['ready', 'Azure tenant guest or employee identity'],
  ['ready', 'Development subscription and resource-group permissions'],
  ['gate', 'Ethizo SMART client registration and test credentials'],
  ['gate', 'Approved test tenant and Group identifier'],
  ['gate', 'Vendor contact, BAA, API terms, data rights, and rate-limit path'],
  ['gate', 'Jody-approved minimum dataset and first workflow'],
  ['gate', 'Security owner and audit-log retention decision']
] as const;

export const evidenceLadder = [
  ['Discover', 'Validate SMART/OIDC metadata and the capability statement.'],
  ['Authorize', 'Obtain a scoped token through the approved client flow.'],
  ['Read', 'Retrieve one approved FHIR resource from the test tenant.'],
  ['Search', 'Prove bounded resource search and pagination.'],
  ['Export', 'Validate approved Group bulk-export behavior where contractually enabled.'],
  ['Land + trace', 'Store immutable evidence with source, version, checksum, lineage, and audit context.'],
  ['Canonicalize', 'Map vendor-specific values to stable eMerge IDs and statuses.'],
  ['Deliver value', 'Expose one useful dashboard, queue, or exception report.']
] as const;

export const sprintDays = [
  ['Day 1', 'Identity, subscriptions, repositories, vendor access, workflow selection'],
  ['Day 2', 'Landing zone, Key Vault, network posture, logging, and scaffold'],
  ['Day 3', 'SMART discovery, token flow, capability, and scope validation'],
  ['Day 4', 'Approved FHIR read/search proof with synthetic or test data'],
  ['Day 5', 'Raw landing, lineage envelope, validation, and quarantine'],
  ['Day 6', 'Canonical facility, referral, and appointment mapping'],
  ['Day 7', 'Operational API and role-specific work queue'],
  ['Day 8', 'Dashboard or exception report with actionable indicators'],
  ['Day 9', 'Security, audit, cost, performance, and failure-path review'],
  ['Day 10', 'Leadership demo, evidence register, decision, and next sprint']
] as const;

export const facilityConfiguration = [
  'Tenant identifiers', 'Specialties', 'Permissions', 'Data mappings', 'Referral rules',
  'Scheduling rules', 'Reporting scope', 'Retention requirements', 'Escalation paths',
  'AI exposure policy', 'Launch gates', 'Vendor strategy'
];

export const unitEconomics = [
  ['Infrastructure cost', 'per active facility'], ['Integration cost', 'per facility / vendor'],
  ['Onboarding labor', 'per launch'], ['Data operations', 'per referral / encounter'],
  ['Model cost', 'per completed task'], ['Support demand', 'per active facility']
] as const;

export const vendorMatrix = [
  ['Clinical workflow', 'Specialty fit, usability, documentation, and exception paths'],
  ['FHIR / HL7 depth', 'Resources, profiles, search, bulk, events, and write-back'],
  ['Security', 'SMART scopes, tenant isolation, audit, BAA, and incident process'],
  ['Data ownership', 'Export rights, retention, portability, and termination support'],
  ['Operations', 'Sandbox, rate limits, SLAs, support, and change notices'],
  ['Economics', 'Facility, provider, transaction, storage, API, and implementation cost']
] as const;

export const documentedEthizo = ['FHIR R4', 'US Core 6.1.0', 'SMART on FHIR', 'OAuth2 / OpenID Connect', 'Read and search', 'Group bulk-export pattern'];
export const ethizoValidationGates = ['Production client credentials', 'Exact tenant scope', 'Complete resource set', 'Write / transaction support', 'Events or webhooks', 'Rate limits and SLAs', 'BAA terms and contractual data rights'];
